import { useState, useEffect, useMemo, Suspense, lazy, type ComponentType } from "react";
import HeroPremium from "@/components/HeroPremium";
import Stats from "@/components/Stats";
import { HomeSectionSkeleton } from "@/components/skeletons";

/**
 * Wrap a dynamic import with one automatic retry after a 1.5 s delay.
 * On slow mobile connections a chunk fetch can fail transiently; a single
 * retry resolves the error without showing the error boundary.
 */
const lazyWithRetry = <T extends ComponentType<unknown>>(
  fn: () => Promise<{ default: T }>
) =>
  lazy(() =>
    fn().catch(
      () =>
        new Promise<{ default: T }>((resolve, reject) =>
          setTimeout(() => fn().then(resolve).catch(reject), 1500)
        )
    )
  );

// Sections below-the-fold : lazy-load avec retry automatique pour mobile
const PopularCars = lazyWithRetry(() => import("@/components/PopularCars"));
const PopularDestinations = lazyWithRetry(() => import("@/components/PopularDestinations"));
const HowItWorks = lazyWithRetry(() => import("@/components/HowItWorks"));
const WhyChooseUs = lazyWithRetry(() => import("@/components/WhyChooseUs"));
const Testimonials = lazyWithRetry(() => import("@/components/Testimonials"));
const FAQ = lazyWithRetry(() => import("@/components/FAQ"));
import { useSEO } from "@/hooks/useSEO";
import { useQuery } from "@tanstack/react-query";
import {
  fetchPopularVehiclesForLocation,
  popularVehiclesQueryKey,
} from "@/lib/queries/popular-vehicles-query";
import { computeMinListingPricePerDay, mergeDynamicListingSeo } from "@/lib/seo/dynamic-listing-seo";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { useStructuredData } from "@/hooks/useStructuredData";
import { SearchResultsDeliveryBanner } from "@/components/search/SearchResultsDeliveryBanner";
import { 
  generateOrganizationSchema, 
  generateWebSiteSchema, 
  generateServiceSchema,
} from "@/lib/seo/structuredData";

const Index = () => {
  const { t, i18n } = useTranslation();
  // Phase 3: Non-blocking auth - homepage renders immediately
  // Auth state is used only for personalization, not blocking
  const { user, getUserRole } = useAuth();
  const [userRole, setUserRole] = useState<string | null>(null);

  const { data: homeListingVehicles } = useQuery({
    queryKey: popularVehiclesQueryKey(undefined),
    queryFn: () => fetchPopularVehiclesForLocation(undefined),
    staleTime: 5 * 60 * 1000,
  });

  const homeMinListingPrice = useMemo(
    () =>
      homeListingVehicles?.length ? computeMinListingPricePerDay(homeListingVehicles) : null,
    [homeListingVehicles]
  );

  const seoLang = i18n.language === "en" ? "en" : "fr";

  useSEO(
    useMemo(
      () => {
        const merged = mergeDynamicListingSeo(
          t("homePage.seo.title"),
          t("homePage.seo.description"),
          homeMinListingPrice,
          seoLang
        );
        return {
          title: merged.title,
          description: merged.description,
          url: "https://rakb.ma/",
          image: "https://rakb.ma/og-image.png?v=2025",
          city: "Casablanca",
          keywords: t("homePage.seo.keywords"),
        };
      },
      [t, homeMinListingPrice, seoLang]
    )
  );

  useStructuredData([
    generateOrganizationSchema(),
    generateWebSiteSchema(),
    generateServiceSchema(),
  ]);

  useEffect(() => {
    const fetchUserRole = async () => {
      if (user) {
        try {
          const role = await getUserRole();
          setUserRole(role);
        } catch (error) {
          console.error("Erreur lors de la récupération du rôle:", error);
        }
      } else {
        setUserRole(null);
      }
    };

    fetchUserRole();
  }, [user, getUserRole]);

  // Phase 3: REMOVED fullscreen auth loader
  // Homepage now renders immediately - auth resolves in background
  // Personalized content appears when auth completes

  return (
    <div className="min-h-screen bg-scandi-bg">
      {/* Message d'accueil personnalisé pour les renters connectés */}
      {user && userRole === "renter" && (
        <div className="border-b border-border/70 bg-white py-2.5">
          <div className="container mx-auto px-4 text-center">
            <p className="text-sm text-text-secondary md:text-base">
              {t('homePage.renterWelcome')}
            </p>
          </div>
        </div>
      )}
      {/* CRO order: hero → cars → social proof → trust → stats → inspiration → process → FAQ */}
      <main id="main-content" className="flex flex-col" tabIndex={-1}>
        <HeroPremium />
        {/* 1. Cars / offers - show product earlier for conversion */}
        <Suspense fallback={<HomeSectionSkeleton />}>
          <PopularCars />
        </Suspense>
        {/* 2. Reviews - reassurance before Why Choose */}
        <Suspense fallback={<HomeSectionSkeleton />}>
          <Testimonials />
        </Suspense>
        {/* 3. Why RAKB / trust */}
        <Suspense fallback={<HomeSectionSkeleton />}>
          <WhyChooseUs />
        </Suspense>
        <SearchResultsDeliveryBanner variant="home" />
        {/* 4. Stats */}
        <Stats />
        {/* 5. Destinations - inspiration */}
        <Suspense fallback={<HomeSectionSkeleton />}>
          <PopularDestinations />
        </Suspense>
        {/* 6. How it works */}
        <Suspense fallback={<HomeSectionSkeleton />}>
          <HowItWorks />
        </Suspense>
        {/* 7. FAQ - near footer */}
        <Suspense fallback={<HomeSectionSkeleton />}>
          <FAQ />
        </Suspense>
      </main>
    </div>
  );
};

export default Index;
